import React, { useState, useEffect, useCallback } from "react";
import "./emp-payroll.css";
import { FaUsers } from "react-icons/fa6";
import { IoEye } from "react-icons/io5";
import { Col, Form, Modal, Button, Spinner, Alert } from "react-bootstrap";
import PayrollLayout from "../../../components/PayrollLayout.jsx";
import { FaRegEdit, FaTrash, FaFileExcel } from "react-icons/fa";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { getPayrollPeriod, getRecentPayrollPeriods } from "../../../utils/payrollPeriod";

const API_URL = "https://backend.ems.uk.hiradentallab.com/api/payroll";

// Load every record of the month at once (no pagination)
const ALL_RECORDS_LIMIT = 10000;

/* ================= EXCEL EXPORT HELPERS ================= */

const ATTENDANCE_API = "https://backend.ems.uk.hiradentallab.com/api/attendance/attendance/range";
const TIMING_API = "https://backend.ems.uk.hiradentallab.com/api/timings";

// Column headers – same order as your payroll sheet (RotaCloud ID removed)
// + "Other Leave" and "Absent" columns so every leave type is shown
const EXPORT_HEADERS = [
  "Payroll ID",
  "First Name",
  "Last Name",
  "Wage",
  "Standard",
  "Overtime",
  "Total",
  "Holiday Days (Paid)",
  "Holiday Days (Unpaid)",
  "Holiday Hours (Paid)",
  "Holiday Hours (Unpaid)",
  "Sickness Days (Paid)",
  "Sickness Days (Unpaid)",
  "Sickness Hours (Paid)",
  "Sickness Hours (Unpaid)",
  "Other Leave Days (Paid)",
  "Other Leave Days (Unpaid)",
  "Other Leave Hours (Paid)",
  "Leave Types",
  "Absent Days",
  "Total Pay",
];

const pick = (obj, keys) => {
  for (const k of keys) {
    const v = k.split(".").reduce((o, p) => o?.[p], obj);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return "";
};

const round2 = (n) => Number((Number(n) || 0).toFixed(2));

// The backend expects the month/year in which the period ENDS (the 25th).
// The middle of the period is always in that month.
const getEndMonthYear = (period) => {
  const middle = new Date(new Date(period.startDate).getTime() + 15 * 24 * 60 * 60 * 1000);
  return { month: middle.getMonth() + 1, year: middle.getFullYear() };
};

// Alphabetical order by employee name (case-insensitive, ignores extra spaces)
const sortByName = (list) =>
  [...list].sort((a, b) =>
    String(a.userName || "").trim().localeCompare(String(b.userName || "").trim(), "en", {
      sensitivity: "base",
    })
  );

// "Tahir Raza Khan" -> First: "Tahir Raza", Last: "Khan"
const splitName = (fullName = "") => {
  const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts.slice(0, -1).join(" "), last: parts[parts.length - 1] };
};

// Shift length in hours from "HH:mm" start / end (handles overnight shifts)
const shiftHoursFromTiming = (timing) => {
  if (!timing?.shiftStart || !timing?.shiftEnd) return 0;
  const [sh, sm] = timing.shiftStart.split(":").map(Number);
  const [eh, em] = timing.shiftEnd.split(":").map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins <= 0) mins += 24 * 60;
  return mins / 60;
};

// Pay period of a payroll record (falls back to the selected payroll period)
const getPeriod = (emp, selectedPeriod) => {
  if (emp.periodStart && emp.periodEnd) {
    return { start: new Date(emp.periodStart), end: new Date(emp.periodEnd) };
  }
  return { start: new Date(selectedPeriod.startDate), end: new Date(selectedPeriod.endDate) };
};

/**
 * Reads the employee's attendance for the pay period and counts
 * holidays, sickness, other leave and absences (as marked in their attendance).
 */
const getLeaveSummary = async (emp, selectedPeriod) => {
  const summary = {
    holidayPaidDays: 0, holidayUnpaidDays: 0, holidayPaidHours: 0, holidayUnpaidHours: 0,
    sickPaidDays: 0, sickUnpaidDays: 0, sickPaidHours: 0, sickUnpaidHours: 0,
    otherPaidDays: 0, otherUnpaidDays: 0, otherPaidHours: 0,
    absentDays: 0,
    leaveTypes: {},
  };

  if (!emp.userId) return summary;

  const { start, end } = getPeriod(emp, selectedPeriod);

  // Attendance records of the period
  const res = await fetch(
    `${ATTENDANCE_API}/${emp.userId}?startDate=${start.toISOString()}&endDate=${end.toISOString()}`
  );
  const json = await res.json();
  const records = json?.success && Array.isArray(json.data) ? json.data : [];

  // Shift hours – used for leave days that have no hours saved (e.g. sickness)
  let shiftHours = 0;
  try {
    const tRes = await fetch(`${TIMING_API}/${emp.userId}`);
    if (tRes.ok) {
      const tJson = await tRes.json();
      shiftHours = shiftHoursFromTiming(tJson.timing || tJson.data || tJson);
    }
  } catch {
    // no timing – hours stay as saved on the record
  }

  const hoursOf = (r) => {
    const saved = parseFloat(r.workedHours);
    return saved > 0 ? saved : shiftHours;
  };

  const countType = (label) => {
    summary.leaveTypes[label] = (summary.leaveTypes[label] || 0) + 1;
  };

  records.forEach((r) => {
    const type = (r.leaveType || "").toLowerCase();

    if (r.status === "holiday") {
      if (type === "unpaid") {
        summary.holidayUnpaidDays += 1;
        summary.holidayUnpaidHours += shiftHours;
        countType("Holiday (unpaid)");
      } else {
        summary.holidayPaidDays += 1;
        summary.holidayPaidHours += hoursOf(r);
        countType("Holiday (paid)");
      }
    } else if (r.status === "leave") {
      if (type === "sick") {
        // The timesheet shows sickness as PAID
        summary.sickPaidDays += 1;
        summary.sickPaidHours += hoursOf(r);
        countType("Sickness");
      } else if (type === "unpaid") {
        summary.otherUnpaidDays += 1;
        countType("Unpaid leave");
      } else {
        // paid / annual / casual / not specified
        summary.otherPaidDays += 1;
        summary.otherPaidHours += hoursOf(r);
        countType(type ? `${type.charAt(0).toUpperCase()}${type.slice(1)} leave` : "Leave");
      }
    } else if (r.status === "absent") {
      summary.absentDays += 1;
    }
  });

  return summary;
};

// Shows numbers for a group only if the group has something, otherwise blank cells
const groupValues = (values) => (values.some((v) => Number(v) > 0) ? values.map(round2) : values.map(() => ""));

const buildExportRow = (emp, leave) => {
  const { first, last } = splitName(emp.userName);

  const standard = Number(emp.totalWorkedHours) || 0;
  const overtime = Number(emp.totalOvertimeHours) || 0;

  const [hPaidD, hUnpaidD, hPaidH, hUnpaidH] = groupValues([
    leave.holidayPaidDays, leave.holidayUnpaidDays, leave.holidayPaidHours, leave.holidayUnpaidHours,
  ]);
  const [sPaidD, sUnpaidD, sPaidH, sUnpaidH] = groupValues([
    leave.sickPaidDays, leave.sickUnpaidDays, leave.sickPaidHours, leave.sickUnpaidHours,
  ]);
  const [oPaidD, oUnpaidD, oPaidH] = groupValues([
    leave.otherPaidDays, leave.otherUnpaidDays, leave.otherPaidHours,
  ]);

  const leaveTypes = Object.entries(leave.leaveTypes)
    .map(([label, count]) => `${label}: ${count}`)
    .join(", ");

  return {
    "Payroll ID": pick(emp, ["payrollId", "payrollNumber"]),
    "First Name": first,
    "Last Name": last,
    "Wage": Number(emp.hourlyRate) > 0 ? "Hourly" : "Salary",
    "Standard": round2(standard),
    "Overtime": round2(overtime),
    "Total": round2(standard + overtime),

    "Holiday Days (Paid)": hPaidD,
    "Holiday Days (Unpaid)": hUnpaidD,
    "Holiday Hours (Paid)": hPaidH,
    "Holiday Hours (Unpaid)": hUnpaidH,

    "Sickness Days (Paid)": sPaidD,
    "Sickness Days (Unpaid)": sUnpaidD,
    "Sickness Hours (Paid)": sPaidH,
    "Sickness Hours (Unpaid)": sUnpaidH,

    "Other Leave Days (Paid)": oPaidD,
    "Other Leave Days (Unpaid)": oUnpaidD,
    "Other Leave Hours (Paid)": oPaidH,
    "Leave Types": leaveTypes,
    "Absent Days": leave.absentDays > 0 ? leave.absentDays : "",

    // Total pay = gross salary (hours × hourly rate), like your sheet
    "Total Pay": round2(emp.grossSalary),
  };
};

export default function EmpPayroll() {
  const [payrollData, setPayrollData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // ===== Payroll period dropdown (current period selected by default) =====
  const [availablePeriods] = useState(() => getRecentPayrollPeriods(12, 1));
  const [selectedPeriod, setSelectedPeriod] = useState(() => getPayrollPeriod());

  /* ---------- Debounce search ---------- */
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  /* ---------- Build query params (shared by table + export) ---------- */
  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    params.append("page", "1");
    params.append("limit", String(ALL_RECORDS_LIMIT));

    if (selectedPeriod) {
      const { month, year } = getEndMonthYear(selectedPeriod);
      params.append("year", String(year));
      params.append("month", String(month));
    }

    if (debouncedSearch) params.append("search", debouncedSearch);
    return params;
  }, [selectedPeriod, debouncedSearch]);

  /* ---------- Fetch ALL payrolls of the month (no pagination) ---------- */
  const fetchPayrolls = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildParams();

      const res = await fetch(`${API_URL}?${params.toString()}`);
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Failed to load payrolls");

      const list = sortByName(data.data || []);
      setPayrollData(list);
      setTotalRecords(data.total || list.length);
    } catch (err) {
      console.error("Error loading payrolls:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchPayrolls();
  }, [fetchPayrolls]);

  /* ---------- Export ALL payrolls of the selected month to Excel ---------- */
  const handleExportExcel = async () => {
    setExporting(true);
    setError(null);
    try {
      const params = buildParams();
      const res = await fetch(`${API_URL}?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load payrolls for export");

      const records = sortByName(data.data || []);
      if (records.length === 0) {
        setError("No payroll data to export for the selected period.");
        return;
      }

      // Read each employee's holidays / leave from their attendance
      const rows = await Promise.all(
        records.map(async (emp) => {
          let leave;
          try {
            leave = await getLeaveSummary(emp, selectedPeriod);
          } catch (e) {
            console.warn("Leave summary failed for", emp.userName, e);
            leave = {
              holidayPaidDays: 0, holidayUnpaidDays: 0, holidayPaidHours: 0, holidayUnpaidHours: 0,
              sickPaidDays: 0, sickUnpaidDays: 0, sickPaidHours: 0, sickUnpaidHours: 0,
              otherPaidDays: 0, otherUnpaidDays: 0, otherPaidHours: 0,
              absentDays: 0, leaveTypes: {},
            };
          }
          return buildExportRow(emp, leave);
        })
      );

      const worksheet = XLSX.utils.json_to_sheet(rows, { header: EXPORT_HEADERS });

      // Column widths
      worksheet["!cols"] = EXPORT_HEADERS.map((h) => ({
        wch: h === "Leave Types" ? 35 : Math.max(12, h.length + 2),
      }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Payroll");
      // e.g. Payroll_26-Sep-2026_to_25-Oct-2026.xlsx
      const fileLabel = (selectedPeriod?.label || "export")
        .replace(/\s*[–-]\s*/g, "_to_")
        .replace(/\s+/g, "-");
      XLSX.writeFile(workbook, `Payroll_${fileLabel}.xlsx`);
    } catch (err) {
      console.error("Export error:", err);
      setError(err.message || "Failed to export payroll");
    } finally {
      setExporting(false);
    }
  };

  const handleViewClick = (emp) => {
    setSelectedEmp(emp);
    setShowModal(true);
  };
  const handleClose = () => setShowModal(false);

  const handlePeriodChange = (e) => {
    const period = availablePeriods.find((p) => p.label === e.target.value);
    if (period) setSelectedPeriod(period);
  };

  const handleDelete = async (payrollId) => {
    if (!window.confirm("Delete this payroll record? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API_URL}/${payrollId}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete");
      fetchPayrolls();
    } catch (err) {
      setError(err.message);
    }
  };

  const getSafeValue = (obj, path, defaultValue = "N/A") => {
    try {
      const value = path.split(".").reduce((o, p) => o?.[p], obj);
      return value !== undefined && value !== null ? value : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const formatDate = (dateString) => {
    try {
      return dateString ? new Date(dateString).toLocaleDateString() : "N/A";
    } catch {
      return "N/A";
    }
  };

  const fmt = (v, d = 2) => (Number(v) || 0).toFixed(d);

  /* ---------- Pay period shown above the table ---------- */
  const formatLongDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

  const getPeriodText = () => {
    // Collect the distinct periods of the records on screen
    const periods = [
      ...new Set(
        payrollData
          .map((emp) =>
            emp.periodLabel ||
            (emp.periodStart && emp.periodEnd
              ? `${formatLongDate(emp.periodStart)} – ${formatLongDate(emp.periodEnd)}`
              : null)
          )
          .filter(Boolean)
      ),
    ];
    if (periods.length > 0) return periods.join(" | ");

    // Fallback: the selected payroll period
    return selectedPeriod?.label || "";
  };

  const periodText = getPeriodText();

  return (
    <PayrollLayout>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h1 className="emps-payroll-title mb-0">
          <FaUsers /> Employees Payroll
        </h1>

        {/* ===== Export to Excel ===== */}
        <Button
          variant="success"
          onClick={handleExportExcel}
          disabled={exporting || loading}
        >
          {exporting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Exporting...
            </>
          ) : (
            <>
              <FaFileExcel className="me-2" />
              Export Payroll
            </>
          )}
        </Button>
      </div>
      <hr />

      <div className="emps-payroll-content">
        <div className="filter-container">
          <Col xs={12} md={4} className="d-flex align-items-center">
            <Form.Select
              value={selectedPeriod.label}
              onChange={handlePeriodChange}
              aria-label="Select payroll period"
            >
              {availablePeriods.map((p) => (
                <option key={p.label} value={p.label}>
                  {p.label}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12} md={8} className="filter-right">
            <Form className="d-flex" onSubmit={(e) => e.preventDefault()}>
              <Form.Control
                type="search"
                placeholder="Search by name or department..."
                className="depts-search-bar"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Form>
          </Col>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center mt-5">
            <Spinner animation="border" /> Loading payroll data...
          </div>
        ) : (
          <div className="table-responsive">
            {/* ===== Pay period shown on top of the table ===== */}
            {periodText && (
              <div
                className="mb-2 px-3 py-2"
                style={{
                  backgroundColor: "#e7f1ff",
                  borderLeft: "4px solid #0d6efd",
                  borderRadius: "6px",
                  fontWeight: 600,
                  color: "#084298",
                }}
              >
                Payroll Period: {periodText}
              </div>
            )}

            {payrollData.length > 0 ? (
              <table className="table table-striped">
                <thead className="table-header">
                  <tr className="emps-payroll-table-header">
                    <th>Name</th>
                    <th>Monthly Salary</th>
                    <th>Contracted Hrs</th>
                    <th>Hourly Rate</th>
                    <th>Worked Hours</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Gross</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {payrollData.map((emp, index) => (
                    <tr key={emp._id || index}>
                      <td>{getSafeValue(emp, "userName")}</td>
                      <td>£{fmt(emp.monthlySalary)}</td>
                      <td>{fmt(emp.contractedHours)}</td>
                      <td>£{fmt(emp.hourlyRate, 4)}</td>
                      <td>{fmt(emp.totalWorkedHours)}</td>
                      <td>{getSafeValue(emp, "presentDays", 0)}</td>
                      <td>{getSafeValue(emp, "absentDays", 0)}</td>
                      <td>£{fmt(emp.grossSalary)}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleViewClick(emp)}
                          >
                            <IoEye />
                          </Button>
                          <Link to={`/edit-payroll/${emp._id}`}>
                            <Button variant="warning" size="sm">
                              <FaRegEdit />
                            </Button>
                          </Link>

                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(emp._id)}
                            disabled={emp.status === "paid"}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-footer">
                  <tr className="emps-payroll-table-footer">
                    <td>Total:</td>
                    <td colSpan={8}>{totalRecords} records</td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <p className="text-center mt-4">No payroll data available for the selected period</p>
            )}
          </div>
        )}

        {/* Modal */}
        <Modal show={showModal} onHide={handleClose} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Payroll Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedEmp && (
              <div>
                <p><strong>Name:</strong> {getSafeValue(selectedEmp, "userName")}</p>
                <p><strong>User ID:</strong> {getSafeValue(selectedEmp, "userId")}</p>
                <p><strong>Department:</strong> {getSafeValue(selectedEmp, "department")}</p>
                <p><strong>Period:</strong> {getSafeValue(selectedEmp, "periodLabel")}</p>
                <hr />
                <p><strong>Monthly Salary:</strong> £{fmt(selectedEmp.monthlySalary)}</p>
                <p><strong>Contracted Hours:</strong> {fmt(selectedEmp.contractedHours)}</p>
                <p><strong>Hourly Rate:</strong> £{fmt(selectedEmp.hourlyRate, 4)}</p>
                <p><strong>Worked Hours:</strong> {fmt(selectedEmp.totalWorkedHours)}</p>
                <p><strong>Present Days:</strong> {getSafeValue(selectedEmp, "presentDays", 0)}</p>
                <p><strong>Absent Days:</strong> {getSafeValue(selectedEmp, "absentDays", 0)}</p>
                <p><strong>Leave Days:</strong> {getSafeValue(selectedEmp, "leaveDays", 0)}</p>
                <p><strong>Overtime Hours:</strong> {fmt(selectedEmp.totalOvertimeHours)}</p>
                <p><strong>Payable Hours:</strong> {fmt(selectedEmp.payableHours)}</p>
                <hr />
                <p><strong>Gross Salary:</strong> £{fmt(selectedEmp.grossSalary)}</p>
                <p><strong>Bonus:</strong> £{fmt(selectedEmp.bonus)}</p>
                <p><strong>Other Deductions:</strong> £{fmt(selectedEmp.otherDeductions)}</p>
                <p><strong>Net Salary:</strong> £{fmt(selectedEmp.netSalary)}</p>
                <p><strong>Status:</strong> {selectedEmp.status}</p>
                <p><strong>Start Date:</strong> {formatDate(selectedEmp.periodStart)}</p>
                <p><strong>End Date:</strong> {formatDate(selectedEmp.periodEnd)}</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </PayrollLayout>
  );
}